import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  AutocompleteItem,
  Button,
  Card,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Skeleton,
  Table,
  TableColumn,
  TableHeader,
  type SharedSelection,
} from '@/components/ui';
import {
  DataTable,
  type Column,
  type Pagination,
} from '@/components/common/table';
import { StandardIcon } from '@/components/common/icon';
import { useApiWithAuth } from '@/hooks/api';
import { useUserAccess } from '@/hooks/auth';
import { useCampuses } from '@/store/org/hooks';
import type { ApiResponse } from '@/shared/types/api';
import type {
  PcoServiceTeamLeaderRef,
  ServicesSyncResult,
} from '@/shared/types/pco/service';
import { Icon } from '@iconify/react';
import type { Campus } from '@/shared/types/campus';

interface ServiceTypeItem {
  pco_id: string;
  name: string;
  mhx_campus_ids?: string[];
  mhx_auto_campus_ids?: string[];
  pco_campus_ids?: string[];
  pco_parent_folder_id?: string | null;
}

interface TeamItem {
  pco_id: string;
  name: string;
  pco_service_type_id: string | null;
  memberships_count: number;
  archived_at: string | Date | null;
  mhx_team_leaders?: PcoServiceTeamLeaderRef[];
}

function automaticCampusLabel(
  serviceType: ServiceTypeItem,
  campuses: Campus[],
): string {
  if (serviceType.mhx_auto_campus_ids?.length) {
    const names = serviceType.mhx_auto_campus_ids.map(
      (id) => campuses.find((campus) => campus.id === id)?.data.name ?? id,
    );

    return `PCO: ${names.join(', ')}`;
  }

  if (serviceType.pco_campus_ids?.length) {
    return 'PCO campus has no MinistryHub match';
  }

  return 'No PCO campus detected';
}

interface TeamRow {
  id: string;
  data: TeamItem;
}

interface TeamMemberPersonItem {
  pco_person_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
}

function personDisplayName(item: TeamMemberPersonItem): string {
  const full = `${item.first_name ?? ''} ${item.last_name ?? ''}`.trim();
  if (full) {
    return full;
  }

  return item.email?.trim() || item.pco_person_id;
}

function personNameOnly(item: TeamMemberPersonItem): string {
  return `${item.first_name ?? ''} ${item.last_name ?? ''}`.trim();
}

function pcoPersonFallbackLabel(id: string): string {
  return `PCO Person ${id}`;
}

function pcoPersonPrimaryLabel(
  id: string,
  person?: TeamMemberPersonItem,
): string {
  return person ? personDisplayName(person) : pcoPersonFallbackLabel(id);
}

function leaderSlotsToPcoPersonIds(
  slots?: PcoServiceTeamLeaderRef[],
): string[] {
  if (!slots?.length) {
    return [];
  }

  return [
    ...new Set(
      slots
        .map((slot) => String(slot.pco_person_id ?? '').trim())
        .filter((id) => id.length > 0),
    ),
  ];
}

function PcoMemberMultiSelect({
  label,
  selected,
  onChange,
  members,
  loading,
}: {
  label: string;
  selected: string[];
  onChange: (ids: string[]) => void;
  members: TeamMemberPersonItem[] | null;
  loading: boolean;
}) {
  const byId = useMemo(
    () =>
      new Map((members ?? []).map((member) => [member.pco_person_id, member])),
    [members],
  );
  const available = useMemo(
    () =>
      (members ?? []).filter(
        (member) => !selected.includes(member.pco_person_id),
      ),
    [members, selected],
  );
  const [input, setInput] = useState('');

  return (
    <Card className='border-1 border-gray-200 px-3 py-2' shadow='none'>
      <label className='text-[75%] font-medium text-foreground-600'>
        {label}
      </label>
      <div className='mt-2 space-y-1'>
        {selected.map((id) => {
          const person = byId.get(id);
          const name = person ? personNameOnly(person) : '';
          const primary = pcoPersonPrimaryLabel(id, person);
          const subtext = name ? person?.email?.trim() || null : null;
          const fallbackText =
            person && !name && primary !== pcoPersonFallbackLabel(id)
              ? pcoPersonFallbackLabel(id)
              : null;
          return (
            <div key={id} className='flex items-start gap-2'>
              <span className='grow'>
                <span className='block text-sm text-slate-700'>{primary}</span>
                {subtext || fallbackText ? (
                  <span className='block text-xs text-slate-500'>
                    {subtext || fallbackText}
                  </span>
                ) : null}
              </span>
              <Button
                isIconOnly
                variant='flat'
                className='bg-transparent text-gray-500'
                onPress={() => onChange(selected.filter((x) => x !== id))}
              >
                x
              </Button>
            </div>
          );
        })}
      </div>
      <Autocomplete
        className='mt-2'
        defaultItems={available}
        isLoading={loading}
        listboxProps={{ portal: true }}
        inputValue={input}
        onInputChange={setInput}
        placeholder='Search team members (PCO people)...'
        selectedKey={null}
        onSelectionChange={(key) => {
          const id = String(key ?? '').trim();
          if (!id || selected.includes(id)) {
            return;
          }
          onChange([...selected, id]);
          setInput('');
        }}
      >
        {(item) => (
          <AutocompleteItem
            key={item.pco_person_id}
            textValue={personDisplayName(item)}
          >
            <div className='flex flex-col'>
              <span>{pcoPersonPrimaryLabel(item.pco_person_id, item)}</span>
              {item.email ? (
                <span className='text-xs text-slate-500'>{item.email}</span>
              ) : null}
            </div>
          </AutocompleteItem>
        )}
      </Autocomplete>
    </Card>
  );
}

const REQUEST_TIMEOUT_MS = 30_000;
const SERVICES_SYNC_TIMEOUT_MS = 600_000;

function withRequestTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out')), ms),
    ),
  ]);
}

const TEAMS_COLUMNS = [
  { key: 'name', label: 'Team' },
  { key: 'type', label: 'Service Type' },
  { key: 'members', label: 'Members' },
  { key: 'leaders', label: 'Team Leaders' },
  { key: 'actions', label: '' },
];

const SKELETON_ROW_COUNT = 5;

function TeamsTableSkeleton({ columns }: { columns: typeof TEAMS_COLUMNS }) {
  return (
    <Table isHeaderSticky className='max-h-[calc(100vh-220px)] text-gray-800'>
      <TableHeader columns={columns}>
        {(col) => <TableColumn key={col.key}>{col.label}</TableColumn>}
      </TableHeader>
      <tbody className='divide-y divide-slate-100'>
        {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
          <tr key={i}>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-48 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-28 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-12 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-24 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-8 w-16 rounded-md' />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export default function AccessTeams() {
  const { invoke, auth } = useApiWithAuth();
  const userAccess = useUserAccess();
  const isAdminUser = Boolean(userAccess?.admin);
  const { list: campuses } = useCampuses();
  const invokeRef = useRef(invoke);
  invokeRef.current = invoke;

  const [serviceTypes, setServiceTypes] = useState<ServiceTypeItem[]>([]);
  const [selectedServiceTypeKey, setSelectedServiceTypeKey] = useState('');
  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const teamsRequestKey = useRef('');

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState<TeamRow | null>(null);
  const [editLeaderPcoIds, setEditLeaderPcoIds] = useState<string[]>([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editMemberPeople, setEditMemberPeople] = useState<
    TeamMemberPersonItem[] | null | undefined
  >(undefined);
  const [syncing, setSyncing] = useState(false);
  const [syncNotice, setSyncNotice] = useState<{
    color: 'success' | 'danger' | 'warning';
    text: string;
  } | null>(null);
  const [campusMappingOpen, setCampusMappingOpen] = useState(false);
  const [campusMappingDraft, setCampusMappingDraft] = useState<
    Record<string, string[] | null>
  >({});
  const [campusMappingSaving, setCampusMappingSaving] = useState(false);
  const [campusMappingError, setCampusMappingError] = useState('');

  const selectedServiceTypeId = useMemo(() => {
    const raw = selectedServiceTypeKey;
    if (!raw) {
      return '';
    }

    return raw.includes(':$') && raw.length > raw.indexOf(':$') + 1
      ? raw.split(':$').pop()!
      : raw;
  }, [selectedServiceTypeKey]);

  const serviceTypeLookup = useMemo(() => {
    return serviceTypes.reduce(
      (lookup, item) => {
        lookup[item.pco_id] = item.name;

        return lookup;
      },
      {} as Record<string, string>,
    );
  }, [serviceTypes]);

  const columns = useMemo(
    (): Column<TeamRow>[] => [
      {
        key: 'name',
        label: 'Team',
        display: ({ item }) => (
          <span className='font-medium text-slate-800'>{item.data.name}</span>
        ),
      },
      {
        key: 'type',
        label: 'Service Type',
        display: ({ item }) => {
          const typeId = item.data.pco_service_type_id;
          const label = typeId ? serviceTypeLookup[typeId] : null;
          if (!label) {
            return <></>;
          }

          return (
            <Chip className='rounded-md border border-slate-200 bg-slate-100 px-3 py-1.5 text-xs text-slate-700'>
              {label}
            </Chip>
          );
        },
      },
      {
        key: 'members',
        label: 'Members',
        display: ({ item }) => <span>{item.data.memberships_count}</span>,
      },
      {
        key: 'leaders',
        label: 'Team Leaders',
        display: ({ item }) => (
          <span>{item.data.mhx_team_leaders?.length ?? 0}</span>
        ),
      },
      {
        key: 'actions',
        label: '',
        display: ({ item }) => (
          <Button
            size='sm'
            variant='flat'
            onPress={() => {
              setEditRow(item);
              setEditLeaderPcoIds(
                leaderSlotsToPcoPersonIds(item.data.mhx_team_leaders),
              );
              setEditError('');
              setEditMemberPeople(null);
              setEditOpen(true);
            }}
          >
            Edit
          </Button>
        ),
      },
    ],
    [serviceTypeLookup],
  );

  useEffect(() => {
    if (!auth.ready) {
      return undefined;
    }

    let cancelled = false;

    const loadServiceTypes = async () => {
      try {
        const response = await withRequestTimeout(
          invokeRef.current('/pco/services/service-types') as Promise<
            ApiResponse<ServiceTypeItem[]>
          >,
          REQUEST_TIMEOUT_MS,
        );
        if (cancelled) {
          return;
        }

        const list =
          response &&
          typeof response === 'object' &&
          'data' in response &&
          Array.isArray(response.data)
            ? response.data
            : [];
        setServiceTypes(list);
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error('Failed loading PCO Services service types:', err);
        setServiceTypes([]);
      }
    };

    void loadServiceTypes();

    return () => {
      cancelled = true;
    };
  }, [auth.ready, reloadToken]);

  useEffect(() => {
    if (!auth.ready) {
      setLoading(false);
      return undefined;
    }

    const requestKey = `${auth.firebaseUser?.uid ?? ''}|${selectedServiceTypeId}|${reloadToken}`;
    if (teamsRequestKey.current === requestKey) {
      setLoading(false);
      return undefined;
    }

    teamsRequestKey.current = requestKey;
    let cancelled = false;

    const loadTeams = async () => {
      setLoading(true);
      setError('');

      try {
        const params = new URLSearchParams();
        params.set('limit', '500');
        if (selectedServiceTypeId) {
          params.set('serviceTypeId', selectedServiceTypeId);
        }

        const response = await withRequestTimeout(
          invokeRef.current(
            `/pco/services/teams?${params.toString()}`,
          ) as Promise<ApiResponse<TeamItem[]>>,
          REQUEST_TIMEOUT_MS,
        );
        if (cancelled) {
          return;
        }

        if (response && typeof response === 'object' && 'data' in response) {
          setTeams(
            (response.data || []).map((item) => ({
              id: item.pco_id,
              data: item,
            })),
          );
        } else {
          setTeams([]);
        }
        setPage(1);
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error('Failed loading PCO Services teams:', err);
        setTeams([]);
        setError(
          err instanceof Error && err.message === 'Request timed out'
            ? 'Request timed out. Try again or use Reload.'
            : 'Unable to load PCO Services teams',
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadTeams();

    return () => {
      cancelled = true;
      teamsRequestKey.current = '';
    };
  }, [auth.ready, auth.firebaseUser?.uid, selectedServiceTypeId, reloadToken]);

  const editPcoIdForMembers = editRow?.data.pco_id;

  useEffect(() => {
    if (!editOpen || !editPcoIdForMembers || !auth.ready) {
      return undefined;
    }

    const pcoId = editPcoIdForMembers;
    let cancelled = false;

    const loadMemberPeople = async () => {
      try {
        const response = await withRequestTimeout(
          invokeRef.current(
            `/pco/services/teams/${encodeURIComponent(pcoId)}/member-people`,
          ) as Promise<ApiResponse<TeamMemberPersonItem[]>>,
          REQUEST_TIMEOUT_MS,
        );
        if (cancelled) {
          return;
        }

        const rawPeople =
          response &&
          typeof response === 'object' &&
          'data' in response &&
          Array.isArray(response.data)
            ? response.data
            : [];
        const normalized = rawPeople
          .map((item) => ({
            pco_person_id: String(item.pco_person_id ?? '').trim(),
            first_name: String(item.first_name ?? '').trim(),
            last_name: String(item.last_name ?? '').trim(),
            email: item.email ? String(item.email).trim().toLowerCase() : null,
          }))
          .filter((item) => item.pco_person_id.length > 0);
        setEditMemberPeople(normalized);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed loading PCO Services team member people:', err);
          setEditMemberPeople(undefined);
        }
      }
    };

    void loadMemberPeople();

    return () => {
      cancelled = true;
    };
  }, [editOpen, editPcoIdForMembers, auth.ready]);

  const onSyncFromPco = useCallback(async () => {
    if (!isAdminUser) {
      return;
    }

    setSyncNotice(null);
    setSyncing(true);
    try {
      const raw = await withRequestTimeout(
        invoke(
          '/pco/services/sync?syncMemberships=true&includeArchived=false',
          {
            method: 'POST',
          },
        ) as Promise<unknown>,
        SERVICES_SYNC_TIMEOUT_MS,
      );

      if (typeof raw === 'string') {
        setSyncNotice({
          color: 'danger',
          text: raw.trim() || 'Sync was rejected or failed.',
        });

        return;
      }

      const response = raw as ApiResponse<ServicesSyncResult | null>;
      const result = response?.data;
      if (result && typeof result === 'object' && 'teams' in result) {
        const summary = [
          `Service types ${result.serviceTypes.synced}/${result.serviceTypes.total}`,
          `Teams ${result.teams.synced}/${result.teams.total}`,
          `Team leaders ${result.teamLeaders.synced}/${result.teamLeaders.total}`,
          `Memberships ${result.memberships.synced}/${result.memberships.total}`,
        ].join(' · ');
        const errCount = result.errors?.length ?? 0;
        let noticeColor: 'success' | 'danger' | 'warning' = 'warning';
        if (result.success) {
          noticeColor = 'success';
        } else if (errCount > 0) {
          noticeColor = 'danger';
        }
        const errSuffix =
          errCount > 0 ? ` ${errCount} error(s) logged on the server.` : '';
        setSyncNotice({
          color: noticeColor,
          text: `${response.message ?? 'Sync finished'}. ${summary}.${errSuffix}`,
        });
        setReloadToken((token) => token + 1);
      } else {
        setSyncNotice({
          color: 'danger',
          text: response?.message ?? 'Sync failed.',
        });
      }
    } catch (e) {
      let failText = 'Sync failed.';
      if (e instanceof Error) {
        failText =
          e.message === 'Request timed out'
            ? 'Sync timed out. It may still be running on the server; try Reload in a few minutes.'
            : e.message;
      }
      setSyncNotice({ color: 'danger', text: failText });
    } finally {
      setSyncing(false);
    }
  }, [invoke, isAdminUser]);

  const onSaveMhx = useCallback(async () => {
    if (!editRow) {
      return;
    }

    setEditSaving(true);
    setEditError('');
    try {
      await invoke(
        `/pco/services/teams/${encodeURIComponent(editRow.data.pco_id)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mhx_team_leaders: editLeaderPcoIds.map((pco_person_id) => ({
              pco_person_id,
            })),
          }),
        },
      );
      setEditOpen(false);
      setEditRow(null);
      setReloadToken((token) => token + 1);
    } catch (e) {
      setEditError(
        e instanceof Error ? e.message : 'Failed to save MinistryHub fields',
      );
    } finally {
      setEditSaving(false);
    }
  }, [editRow, editLeaderPcoIds, invoke]);

  const openCampusMappings = useCallback(() => {
    setCampusMappingDraft(
      Object.fromEntries(
        serviceTypes.map((serviceType) => [
          serviceType.pco_id,
          Array.isArray(serviceType.mhx_campus_ids)
            ? [...serviceType.mhx_campus_ids]
            : null,
        ]),
      ),
    );
    setCampusMappingError('');
    setCampusMappingOpen(true);
  }, [serviceTypes]);

  const onSaveCampusMappings = useCallback(async () => {
    const changed = serviceTypes.filter((serviceType) => {
      const current = Array.isArray(serviceType.mhx_campus_ids)
        ? [...serviceType.mhx_campus_ids].sort()
        : null;
      const draft = Array.isArray(campusMappingDraft[serviceType.pco_id])
        ? [...(campusMappingDraft[serviceType.pco_id] ?? [])].sort()
        : null;

      return current?.join('|') !== draft?.join('|');
    });

    if (changed.length === 0) {
      setCampusMappingOpen(false);
      return;
    }

    setCampusMappingSaving(true);
    setCampusMappingError('');
    try {
      await Promise.all(
        changed.map(async (serviceType) => {
          const raw = await invoke(
            `/pco/services/service-types/${encodeURIComponent(serviceType.pco_id)}`,
            {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                mhx_campus_ids: campusMappingDraft[serviceType.pco_id] ?? null,
              }),
            },
          );
          if (typeof raw === 'string') {
            throw new Error(raw || `Failed to update ${serviceType.name}`);
          }
        }),
      );

      setServiceTypes((current) =>
        current.map((serviceType) => {
          const draft = campusMappingDraft[serviceType.pco_id];
          if (draft === null || draft === undefined) {
            const { mhx_campus_ids: _manual, ...automatic } = serviceType;

            return automatic;
          }

          return { ...serviceType, mhx_campus_ids: [...draft] };
        }),
      );
      setCampusMappingOpen(false);
    } catch (err) {
      setCampusMappingError(
        err instanceof Error ? err.message : 'Failed to save campus mappings',
      );
    } finally {
      setCampusMappingSaving(false);
    }
  }, [campusMappingDraft, invoke, serviceTypes]);

  const pagination: Pagination = useMemo(
    () => ({
      enabled: true,
      absolute: true,
      page,
      itemsPerPage,
      allowItemsPerPage: [10, 20, 50, 100],
      onPageChange: (nextPage: number) => setPage(nextPage),
      onItemsPerPageChange: (value: number) => {
        setItemsPerPage(value);
        setPage(1);
      },
    }),
    [page, itemsPerPage],
  );

  const onServiceTypeChange = (selection: SharedSelection) => {
    setSelectedServiceTypeKey(selection.currentKey || '');
  };

  const startIndex = (page - 1) * itemsPerPage;
  const displayedItems = useMemo(
    () => teams.slice(startIndex, startIndex + itemsPerPage),
    [teams, startIndex, itemsPerPage],
  );

  const showSkeleton = loading && teams.length === 0;

  return (
    <div>
      <div className='mb-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center'>
        <Select
          className='min-w-[220px] basis-1/3'
          aria-label='Filter teams by service type'
          selectedKeys={new Set([selectedServiceTypeKey])}
          onSelectionChange={onServiceTypeChange}
          placeholder='All Service Types'
        >
          <SelectItem key=''>All Service Types</SelectItem>
          {serviceTypes.map((item) => (
            <SelectItem key={item.pco_id}>{item.name}</SelectItem>
          ))}
        </Select>
        <Button
          variant='light'
          onPress={() => setReloadToken((token) => token + 1)}
          startContent={<StandardIcon id='mdi:reload' />}
        >
          Reload
        </Button>
        {isAdminUser ? (
          <>
            <Button
              variant='flat'
              isDisabled={!auth.ready || serviceTypes.length === 0}
              onPress={openCampusMappings}
              startContent={<StandardIcon id='mdi:map-marker-multiple' />}
            >
              Campus mappings
            </Button>
            <Button
              color='primary'
              variant='flat'
              isDisabled={!auth.ready || syncing}
              isLoading={syncing}
              onPress={() => void onSyncFromPco()}
              startContent={
                syncing ? undefined : (
                  <StandardIcon id='mdi:cloud-sync-outline' />
                )
              }
            >
              Sync from PCO
            </Button>
          </>
        ) : null}
      </div>
      {syncNotice ? (
        <Alert color={syncNotice.color} className='mb-2'>
          {syncNotice.text}
        </Alert>
      ) : null}
      {error ? (
        <Alert color='danger' className='mb-2'>
          {error}
        </Alert>
      ) : null}
      {showSkeleton ? (
        <TeamsTableSkeleton columns={TEAMS_COLUMNS} />
      ) : (
        <DataTable
          aria-label='PCO Services Teams'
          columns={columns}
          items={displayedItems}
          count={teams.length}
          pagination={pagination}
          paginationVariant='numbered'
          loading={loading}
          className='text-gray-800'
          empty={{
            icon: 'mdi:account-wrench-outline',
            bottomLeftIcon: 'material-symbols:group-off-outline',
            title: 'No teams found',
            body: selectedServiceTypeId
              ? "We couldn't find any teams for the selected service type. Try choosing a different service type or clear the filter."
              : "We couldn't find any teams. Try reloading or check your connection.",
            actions: (
              <>
                <Button
                  variant='flat'
                  onPress={() => setSelectedServiceTypeKey('')}
                  isDisabled={!selectedServiceTypeKey}
                >
                  Clear filter
                </Button>
                <Button
                  variant='flat'
                  onPress={() => setReloadToken((token) => token + 1)}
                  startContent={
                    <Icon icon='mdi:reload' width={18} height={18} />
                  }
                >
                  Reload
                </Button>
              </>
            ),
          }}
        />
      )}
      <Modal
        isOpen={editOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEditOpen(false);
            setEditRow(null);
            setEditMemberPeople(undefined);
          }
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>MinistryHub - PCO Services team</ModalHeader>
              <ModalBody className='gap-4'>
                {editRow ? (
                  <p className='text-sm text-slate-600'>
                    <span className='font-semibold'>{editRow.data.name}</span>
                  </p>
                ) : null}
                {Array.isArray(editMemberPeople) &&
                editMemberPeople.length === 0 ? (
                  <Alert color='warning' className='text-sm'>
                    No PCO Services team members found in Mongo for this team.
                    Run a Services sync first; leader pickers stay empty until
                    members are synced.
                  </Alert>
                ) : null}
                <PcoMemberMultiSelect
                  key={`team-leaders-pco-${editRow?.id ?? 'none'}`}
                  label='Team leaders'
                  selected={editLeaderPcoIds}
                  onChange={setEditLeaderPcoIds}
                  members={editMemberPeople ?? null}
                  loading={editMemberPeople === null}
                />
                <p className='text-xs text-slate-500'>
                  Pickers store PCO person ids for members in this Services
                  team. The backend links `mhx_user_id` using the same lookup as
                  the Services sync.
                </p>
                {editError ? <Alert color='danger'>{editError}</Alert> : null}
              </ModalBody>
              <ModalFooter>
                <Button variant='flat' onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  color='primary'
                  isLoading={editSaving}
                  onPress={() => void onSaveMhx()}
                >
                  Save
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      <Modal
        isOpen={campusMappingOpen}
        size='2xl'
        scrollBehavior='inside'
        onOpenChange={(open) => {
          if (!open && !campusMappingSaving) {
            setCampusMappingOpen(false);
            setCampusMappingError('');
          }
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>PCO Services campus mappings</ModalHeader>
              <ModalBody className='gap-4'>
                <p className='text-sm text-slate-600'>
                  Campus assignments are detected from each service type's PCO
                  folder. You can override that assignment here when needed.
                </p>
                <div className='space-y-3'>
                  {serviceTypes.map((serviceType) => (
                    <div
                      key={serviceType.pco_id}
                      className='grid gap-2 rounded-lg border border-slate-200 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(260px,1.5fr)] sm:items-center'
                    >
                      <div>
                        <span className='font-medium text-slate-800'>
                          {serviceType.name}
                        </span>
                        <span className='mt-1 block text-xs text-slate-500'>
                          {automaticCampusLabel(serviceType, campuses)}
                        </span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <Select
                          className='grow'
                          aria-label={`Campuses for ${serviceType.name}`}
                          selectionMode='multiple'
                          placeholder='No campuses mapped'
                          selectedKeys={
                            new Set(
                              campusMappingDraft[serviceType.pco_id] ??
                                serviceType.mhx_auto_campus_ids ??
                                [],
                            )
                          }
                          onSelectionChange={(selection) => {
                            const ids =
                              selection === 'all'
                                ? campuses.map((campus) => campus.id)
                                : Array.from(selection).map(String);
                            setCampusMappingDraft((current) => ({
                              ...current,
                              [serviceType.pco_id]: ids,
                            }));
                          }}
                        >
                          {campuses.map((campus) => (
                            <SelectItem key={campus.id}>
                              {campus.data.name}
                            </SelectItem>
                          ))}
                        </Select>
                        {campusMappingDraft[serviceType.pco_id] !== null ? (
                          <Button
                            size='sm'
                            variant='light'
                            onPress={() =>
                              setCampusMappingDraft((current) => ({
                                ...current,
                                [serviceType.pco_id]: null,
                              }))
                            }
                          >
                            Use PCO
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
                {campusMappingError ? (
                  <Alert color='danger'>{campusMappingError}</Alert>
                ) : null}
              </ModalBody>
              <ModalFooter>
                <Button
                  variant='flat'
                  isDisabled={campusMappingSaving}
                  onPress={onClose}
                >
                  Cancel
                </Button>
                <Button
                  color='primary'
                  isLoading={campusMappingSaving}
                  onPress={() => void onSaveCampusMappings()}
                >
                  Save mappings
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
